/* eslint-disable complexity */
import PropTypes from 'prop-types';
import React from 'react';

import { getImage } from 'gatsby-plugin-image';

import { propTypeslocalization } from '../../../constants/Proptypes';

import Button, {
  ButtonAppearance,
  ButtonTheme,
} from '../../../../ui/Atoms/Button';
import Icon, { IconSize } from '../../../../ui/Atoms/Icon';
import Text, { TextAppearance } from '../../../../ui/Atoms/Text';

import { useSize } from '../../../hooks/useSize';
import { getIcon } from '../../../utils/getIcon';

import { Color } from '../../../constants/Colors';
import * as S from './PromoBanner.styles';

const Picture = React.lazy(() => import('../../../../ui/Atoms/Picture'));

const Themes = {
  Blue: Color.ANAKIWA,
  Pink: Color.CARNATION_PINK,
  Purple: Color.PERFUME,
};

const IconColors = {
  Blue: Color.CLOUD_BURST,
  Pink: Color.BRILLIANT_ROSE,
  Purple: Color.CORNFLOWER_BLUE,
};

export const PromoBanner = ({ localization }) => {
  const { data } = localization || {};
  const screenWidth = useSize().width;

  const isMobile = screenWidth <= 768;

  if (!data) {
    return null;
  }

  const {
    banner_background: { alt: backgroundAlt },
    banner_cta_datatrack_attribute: datatrackAttribute,
    banner_cta_label: label,
    banner_cta_link: { target, uid, url: link },
    banner_icon: icon,
    banner_illustration: { alt: illustrationAlt },
    banner_subtitle: { html: subtitle },
    banner_theme: theme,
    banner_title: title,
    is_banner_negative: isBannerNegative,
  } = data || '';

  if (!title) {
    return null;
  }

  const themeColor = Themes[theme] || Themes.Blue;
  const iconColor = IconColors[theme] || IconColors.Blue;
  const isCentered = (!label || !link) && icon === 'None';
  const hasSubtitleOnMobile = isMobile && (!link || !label);

  const imageBackground = getImage(data.banner_background);
  const imageIllustration = getImage(data.banner_illustration);

  const buttonTheme = isBannerNegative ? ButtonTheme.WOOCLAP : ButtonTheme.DARK;
  const buttonAppearance = isBannerNegative
    ? ButtonAppearance.OUTLINE
    : ButtonAppearance.PRIMARY;

  return (
    <S.Container>
      {imageBackground && (
        <React.Suspense fallback='Loading...'>
          <Picture
            altText={backgroundAlt}
            background={themeColor}
            imageObject={imageBackground}
            width='100%'
          />
        </React.Suspense>
      )}
      {imageIllustration && !isMobile && (
        <S.IllustrationWrapper>
          <React.Suspense fallback='Loading...'>
            <Picture
              altText={illustrationAlt}
              imageObject={imageIllustration}
            />
          </React.Suspense>
        </S.IllustrationWrapper>
      )}
      <S.ContentWrapper>
        {icon !== 'None' && !imageIllustration && !isMobile && (
          <Icon
            backgroundColor={isBannerNegative ? Color.WHITE : iconColor}
            customColor={isBannerNegative ? Color.CLOUD_BURST : Color.WHITE}
            size={IconSize.REDUCED}
          >
            {getIcon(icon)}
          </Icon>
        )}
        <S.Content isCentered={isCentered}>
          <S.Title isNegative={isBannerNegative}>{title}</S.Title>
          {subtitle && (!isMobile || hasSubtitleOnMobile) && (
            <Text
              color={isBannerNegative ? Color.WHITE : Color.CLOUD_BURST}
              type={isMobile ? TextAppearance.INFO : TextAppearance.NORMAL}
            >
              {subtitle}
            </Text>
          )}
        </S.Content>
        {link && label && (
          <S.ButtonWrapper isNegative={isBannerNegative}>
            <Button
              appearance={buttonAppearance}
              dataTrackAttribute={datatrackAttribute}
              isGatsbyLink={!!uid}
              link={link}
              target={target}
              theme={buttonTheme}
            >
              {label}
            </Button>
          </S.ButtonWrapper>
        )}
      </S.ContentWrapper>
    </S.Container>
  );
};

PromoBanner.propTypes = {
  /**
   * Localization document type
   */
  localization: PropTypes.shape(propTypeslocalization),
};
